import React, { useContext, useEffect, useState } from "react";
import Gallery from "../../../components/Gallery/Gallery";
import AttachmentApi from "../../../api/services/AttachmentApi";
import AttachmentFileHandler from "../../../helper/AttachmentFileHandler";
import FileReference from "../../../components/Gallery/model/FileReference.model";
import LoadedFile from "../../../components/Gallery/model/LoadedFile.model";
import SubstrateSamplesApi from "../../../api/services/SubstrateSamplesApi";
import Attachment from "../../../components/Gallery/model/Attachment.model";
import GalleryContext from "../../../contexts/GalleryContext";
import SubstrateAttachment from "../../../api/models/substrate/SubstrateAttachment";
import useEnv from "../../../hooks/env";
import TestRequestApi from "../../../api/services/TestRequestApi";

export interface SubstrateSampleGalleryProps {
  editable: boolean;
  testRequestId: string;
  substrateSampleId: number;
  substrateSampleName: string;
  substrateAttachments: SubstrateAttachment[];
  onChange: (value: SubstrateAttachment[]) => void;
}

const SubstrateSampleGallery: React.FC<SubstrateSampleGalleryProps> = (props) => {
  const { editable, substrateAttachments, onChange, substrateSampleId, substrateSampleName, testRequestId } = props;
  const { flags } = useContext(GalleryContext);
  const { api } = useEnv();
  const attachmentApi = new AttachmentApi(api);
  const testRequestApi = new TestRequestApi(api);
  const substrateSamplesApi = new SubstrateSamplesApi(api);
  const mapAttachment = (value: SubstrateAttachment): Attachment => ({
    ...value,
    flag: flags?.find((f) => f.type === value.type),
  });
  const mapSubstrateAttachment = (value: Attachment): SubstrateAttachment => ({
    ...value,
    type: value.flag?.type,
  });

  const namingStrategy = {
    nameDetailDialog(file: LoadedFile) {
      return {
        header: `GALLERY ${substrateSampleName}`,
        subheader: file.name,
      };
    },
  };

  const attachmentFileHandler = new AttachmentFileHandler(
    attachmentApi,
    {
      remove: (idToBeDeleted) => {
        return substrateSamplesApi
          .removeAttachment(testRequestId, substrateSampleId, idToBeDeleted)
          .then((values) => values.map(mapAttachment))
          .then((attachments) => ({ attachments, idToBeDeleted }));
      },
      append: (ids, flag) =>
        substrateSamplesApi
          .addAttachment(testRequestId, substrateSampleId, ids, flag?.type || "OTHER")
          .then((values) => values.map(mapAttachment)),
      zipFileName(files: FileReference[]): string {
        if (files.length === 1) {
          const file = files[0];
          if (file.status === "LOADED") {
            return (file as LoadedFile).name;
          }
          return file.id;
        }
        return `LEAP-${testRequestId}-${substrateSampleName.replace(/[^A-Za-z0-9]/g, "_")}.zip`;
      },
    },
    () => testRequestApi.getTestRequestAttachmentClearanceToken(testRequestId).then((res) => res.data),
  );

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  useEffect(() => {
    setAttachments(substrateAttachments.map(mapAttachment));
  }, [substrateAttachments]);

  return (
    <Gallery
      disabled={!editable}
      hideIfEmpty
      galleryId={`SubstrateSample${substrateSampleId}`}
      fileHandler={attachmentFileHandler}
      namingStrategy={namingStrategy}
      attachments={attachments}
      onChange={(values) => onChange(values.map(mapSubstrateAttachment))}
      testRequestId={testRequestId}
    />
  );
};

export default SubstrateSampleGallery;
